import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Asset, Box, Typography, Svg, ResponsiveValue, Colors, Theme } from '@altermaind/web/ui';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';

type UserProps = {
  username: string;
  role: string;
  image?: string;
  width?: string;
  children?: ReactNode;
  backgroundColor?: ResponsiveValue<keyof Colors, Theme> | undefined;
  onClose?: () => void;
};

export const UserInfoComponent: FC<UserProps> = ({
  username,
  role,
  image,
  width,
  children,
  backgroundColor = 'primary/inactive',
  onClose,
}) => {
  const { isMobile } = useBreakingPoints();

  const [dropdown, setDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDropdown = () => {
    if (children !== undefined) {
      if (dropdown) onClose?.();
      setDropdown(!dropdown);
    }
  };

  const abbreviation = useMemo(() => {
    if (username.includes(' ')) {
      const first = username.split(' ');
      return first[0].charAt(0) + first[1].charAt(0);
    }
    return username.charAt(0);
  }, [username]);

  useEffect(() => {
    if (!dropdown) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdown(false);
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdown, onClose]);

  return (
    <Box
      ref={containerRef}
      display='flex'
      alignItems='center'
      width={isMobile ? '100%' : '250px'}
      position='relative'
    >
      {image ? (
        <Asset domain='images' name={image} data-testid='user-logo' width={60} height={60} />
      ) : (
        <Box
          bg={backgroundColor}
          display='flex'
          flexDirection='column'
          borderRadius='m'
          alignItems='center'
          justifyContent='center'
          width={60}
          height={60}
        >
          <Typography
            display='inline-flex'
            justifyContent='center'
            alignItems='center'
            variant='special'
          >
            {abbreviation.toUpperCase()}
          </Typography>
        </Box>
      )}
      <Box ml='xs' flex={1} height={!role ? '100%' : undefined}>
        <Typography
          variant='p1/bold'
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          onClick={handleDropdown}
          data-testid='user-logo'
          cursor='pointer'
        >
          {!isMobile ? username : ''}{' '}
          {children && (
            <Box
              ml='s'
              display='flex'
              alignItems='center'
              justifyContent='center'
              style={{
                transition: 'transform 0.2s',
                transform: dropdown ? 'rotate(180deg)' : undefined,
              }}
            >
              <Svg name='down' />
            </Box>
          )}
        </Typography>
        {!isMobile && (
          <Typography variant='p2' data-testid='user-role'>
            {role}
          </Typography>
        )}
        {dropdown && (
          <Box
            top='calc(100% + 38px)'
            position='absolute'
            width={width ?? 'auto'}
            right='0'
            zIndex='999999'
          >
            {children}
          </Box>
        )}
      </Box>
    </Box>
  );
};
